import React, { useEffect, useRef, useState } from "react";
import { Pagination } from "./Pagination";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { Network, RefreshCw, Search, ChevronUp, ChevronDown, Loader2 } from "lucide-react";

interface OpenPortsProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const DEFAULT_PER_PAGE = 10;

const OpenPorts: React.FC<OpenPortsProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [ports, setPorts] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
    const [total, setTotal] = useState<number>(0);
    const [search, setSearch] = useState<string>("");
    const [searchInput, setSearchInput] = useState<string>("");
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [loading, setLoading] = useState(true);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);
    const fetchPorts = async (
        skip: number = 0,
        limit: number = perPage,
        searchTerm: string = search,
        currentSort = sortConfig
    ) => {
        setLoading(true);
        try {
            const queryObj: any = { client_id, organization_id, agent_id, asset_id };
            if (searchTerm.trim()) {
                const isNumber = /^\d+$/.test(searchTerm.trim());

                if (isNumber) {
                    // ✅ Numeric input → search port only
                    queryObj.port = Number(searchTerm.trim());
                } else {
                    // ✅ String input → search name, service_name, address
                    queryObj.$or = [
                        { process_name: { $ilike: `%${searchTerm.trim()}%` } },
                        { service_name: { $ilike: `%${searchTerm.trim()}%` } },
                        { address: { $ilike: `%${searchTerm.trim()}%` } },
                        { state: { $ilike: `%${searchTerm.trim()}%` } },
                        { protocol: { $ilike: `%${searchTerm.trim()}%` } },
                        { name: { $ilike: `%${searchTerm.trim()}%` } },
                    ];
                }
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                status: "open",
                skip: skip,
                limit: limit,
            };

            // Add API Sorting if requested
            if (currentSort) {
                params.sort_by = currentSort.key;
                params.sort_order = currentSort.direction;
            }

            const response = await baseapi.get("/asset_internal_ports", { params });
            setPorts(response.data?.data ?? []);
            setTotal(response.data?.total ?? 0);
        } catch (err: any) {
            showError(err.message);
            setPorts([]);
            setTotal(0);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchPorts(0, perPage, "", sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client_id, organization_id, agent_id, asset_id]);

    // const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    //     if (e.key === "Enter") {
    //         const trimmedSearch = searchInput.trim();
    //         setSearch(trimmedSearch);
    //         setPage(1);
    //         fetchPorts(0, perPage, trimmedSearch, sortConfig);
    //     }
    // };
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        debounceTimer.current = setTimeout(() => {
            const trimmed = value.trim();
            setSearch(trimmed);
            setPage(1);
            fetchPorts(0, perPage, trimmed, sortConfig);
        }, 400);
    };

    // const handleSearchButton = () => {
    //     const trimmedSearch = searchInput.trim();
    //     setSearch(trimmedSearch);
    //     setPage(1);
    //     fetchPorts(0, perPage, trimmedSearch, sortConfig);
    // };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= Math.ceil(total / perPage)) {
            setPage(newPage);
            fetchPorts((perPage * (newPage - 1)), perPage, search, sortConfig);
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setPerPage(value);
        setPage(1);
        fetchPorts(0, value, search, sortConfig);
    };

    const handleRefresh = () => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        setSearch("");
        setSearchInput("");
        setPage(1);
        fetchPorts(0, perPage, "", sortConfig);
    };

    const handleSort = (key: string) => {
        let newDirection: "asc" | "desc" = "asc";
        if (sortConfig && sortConfig.key === key) {
            newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
        }
        const newSortConfig = { key, direction: newDirection };
        setSortConfig(newSortConfig);
        setPage(1); // Reset to page 1 on sort
        fetchPorts(0, perPage, search, newSortConfig);
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, widthClass }: { label: string; sortKey: string; widthClass?: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`${widthClass} px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 cursor-pointer group select-none`}
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-blue-400" />
                            ) : (
                                <ChevronDown size={12} className="text-blue-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[300px] w-full p-4">
                <div className="bg-gray-900/40 border border-gray-700 rounded-md p-8 flex flex-col items-center justify-center w-full shadow-lg">
                    <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-3" />
                    <p className="text-gray-200 text-xs font-semibold uppercase tracking-widest animate-pulse">Loading Open Ports...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-3 border-b border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Network className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white leading-tight">Open Ports</h2>
                        <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {total || 0} open ports found
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={searchInput}
                            onChange={handleSearchChange}
                            placeholder="Search port or name..."
                            className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                        />
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden">
                <table className="w-full table-fixed">
                    <thead className="bg-gray-800/80">
                        <tr>
                            <SortableHeader widthClass="w-[8%]" label="Port" sortKey="port" />
                            <SortableHeader widthClass="w-[15%]" label="Address" sortKey="address" />
                            <SortableHeader widthClass="w-[12%]" label="Name" sortKey="name" />
                            <SortableHeader widthClass="w-[10%]" label="Protocol" sortKey="protocol" />
                            <SortableHeader widthClass="w-[15%]" label="Service" sortKey="service_name" />
                            <SortableHeader widthClass="w-[30%]" label="Path" sortKey="path" />
                            <th className="w-[10%] px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="py-8 text-center text-gray-200 text-[11px]">Loading ports...</td>
                            </tr>
                        ) : ports.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="py-8 text-center">
                                    <div className="flex flex-col items-center opacity-50">
                                        <Network className="w-8 h-8 mb-2 text-green-400" />
                                        <span className="font-bold text-sm text-green-400">No open ports found</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            ports.map((port, idx) => (
                                <tr key={port.port ?? idx} className="hover:bg-gray-800/40 transition-colors duration-100">
                                    <td className="px-3 py-2 text-white text-[11px] font-mono font-bold">{port.port}</td>
                                    <td className="px-3 py-2 text-gray-300 text-[11px] font-mono truncate" title={port.address}>{port.address || '-'}</td>
                                    <td className="px-3 py-2 text-gray-300 text-[11px] font-mono truncate" title={port.name}>{port.name}</td>
                                    <td className="px-3 py-2">
                                        {port.protocol ? (
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${port.protocol.toLowerCase() === "tcp"
                                                ? "text-blue-400 border-blue-500/20 bg-blue-500/10"
                                                : "text-green-400 border-green-500/20 bg-green-500/10"
                                                }`}>
                                                {port.protocol.toUpperCase()}
                                            </span>
                                        ) : (
                                            <span className="text-gray-600">-</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-gray-300 text-[11px] truncate" title={port.service_name}>{port.service_name || '-'}</td>
                                    <td className="px-3 py-2 text-gray-200 text-[10px] font-mono truncate" title={port.path}>{port.path || '-'}</td>
                                    <td className="px-3 py-2">
                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold border text-green-400 border-green-500/20 bg-green-500/10 uppercase">
                                            Open
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot className="bg-gray-900/60">
                        <tr>
                            <td colSpan={7} className="p-0">
                                <Pagination
                                    currentPage={page}
                                    totalPages={Math.max(1, Math.ceil(total / perPage))}
                                    onPageChange={handlePageChange}
                                    itemsPerPage={perPage}
                                    totalItems={total}
                                    onItemsPerPageChange={handleItemsPerPageChange}
                                />
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default OpenPorts;